import { UserRound } from 'lucide-react';
import { SearchResult } from '#server/models';
import {
  ResultCard,
  ResultCardHeader,
  ResultCardTitle,
  ResultCardLink,
  ResultCardTag,
  ResultCardContent,
  ResultCardDescription,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Badge,
  Divider,
} from '@/components';
import { cn } from '@/utils';

export interface PeopleResultCardProps {
  result: SearchResult;
}

export const PeopleResultCard = ({ result }: PeopleResultCardProps) => {
  const personDetails = result.personDetails!;

  return (
    <ResultCardLink to={personDetails.url} result={result}>
      <ResultCard className={cn('md:flex-row', (result.forYou ?? false) && 'border-highlight-border bg-highlight-bg')}>
        <ResultCardHeader className="mb-4 md:mb-0 md:shrink-0 md:grow-0 md:basis-2/5 md:pr-6">
          <ResultCardTag>
            {(result.forYou ?? false) && <Badge className="mr-2 bg-ring text-white">For You</Badge>}
            <Badge variant={(result.forYou ?? false) ? 'forYou' : 'secondary'}>People</Badge>
          </ResultCardTag>
          <div className="flex items-center">
            <Avatar className="mr-4 md:mr-6">
              <AvatarImage src={personDetails.imageUrl} />
              <AvatarFallback>
                <UserRound className="size-6 md:size-7" />
              </AvatarFallback>
            </Avatar>
            <div>
              <ResultCardTitle>{personDetails.name}</ResultCardTitle>
              <ResultCardDescription>{personDetails.staffType.join(', ')}</ResultCardDescription>
            </div>
          </div>
        </ResultCardHeader>
        {(personDetails.primaryOrgUnit || personDetails.location || personDetails.email) && (
          <>
            <Divider className={cn((result.forYou ?? false) && 'bg-highlight-divider')} orientation="vertical" />
            <ResultCardContent
              className={cn(
                'mt-0 flex grow flex-col gap-2 md:grid md:grid-cols-2',
                personDetails.primaryOrgUnit && personDetails.location ? 'md:grid-cols-2' : 'md:grid-cols-1',
              )}
            >
              {personDetails.primaryOrgUnit && (
                <div
                  className={cn(
                    personDetails.location ? 'col-span-1 md-to-lg:col-span-2' : 'col-span-2',
                    'flex items-center rounded-lg bg-primary-website-background-colour px-5 py-3 text-sm',
                    (result.forYou ?? false) && 'bg-white',
                  )}
                >
                  <div className="flex items-center gap-8">
                    <div className="w-8 font-semibold">{personDetails.primaryOrgUnit.type}</div>
                    <div className="text-secondary-grey-01">{personDetails.primaryOrgUnit.name}</div>
                  </div>
                </div>
              )}
              {personDetails.location && (
                <div
                  className={cn(
                    personDetails.primaryOrgUnit ? 'col-span-1 md-to-lg:col-span-2' : 'col-span-2',
                    'flex items-center rounded-lg bg-primary-website-background-colour px-5 py-3 text-sm',
                    (result.forYou ?? false) && 'bg-white',
                  )}
                >
                  <div className="flex items-center gap-8">
                    <div className="w-8 font-semibold">Location</div>
                    <div className="text-secondary-grey-01">{personDetails.location}</div>
                  </div>
                </div>
              )}
              {personDetails.email && (
                <div
                  className={cn(
                    'col-span-2 flex items-center rounded-lg bg-primary-website-background-colour px-5 py-3 text-sm',
                    (result.forYou ?? false) && 'bg-white',
                  )}
                >
                  <div className="flex items-center gap-8">
                    <div className="w-8 font-semibold">Email</div>
                    <div className="break-all underline hover:text-ring focus:text-ring">
                      <a
                        href={`mailto:${personDetails.email}`}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {personDetails.email}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </ResultCardContent>
          </>
        )}
      </ResultCard>
    </ResultCardLink>
  );
};
